/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

import { Emitter, Event } from '../../../base/common/event.js';
import { Disposable, IDisposable, MutableDisposable } from '../../../base/common/lifecycle.js';
import { IElementData, IBrowserViewTheme, IBrowserViewRect } from '../common/browserView.js';
import { ICDPConnection } from '../common/cdp/types.js';
import type { BrowserView } from './browserView.js';
import { BrowserViewFrameInspector } from './browserViewFrameInspector.js';

interface IActiveSelection extends IDisposable {
}

export interface IElementHandle extends IDisposable {
	addToChat(): Promise<void>;
	highlight(): Promise<void>;
	hideHighlight(): Promise<void>;
}

/**
 * Well-known ids understood by `__vscode_helpers.getElement(id)` in
 * `preload-browserView.ts`. Any other string is treated as the id of a
 * dynamically tracked element.
 */
export const enum BrowserViewInspectElementId {
	/** The page's `document.activeElement`. */
	Active = 'active',
	/** The element targeted by the most recent `contextmenu` event. */
	ContextMenuTarget = 'context-menu-target',
}

/**
 * Manages element inspection across all frames in a browser view.
 *
 * Creates a {@link BrowserViewFrameInspector} for the main frame and
 * automatically discovers iframe CDP targets via auto-attach, matching
 * them to their corresponding `WebFrameMain` instances using an opaque
 * token generated by the preload script.
 *
 * This class is a thin orchestrator — all per-frame CDP logic (domain
 * initialization, element extraction, CDP inspect mode) lives in
 * {@link BrowserViewFrameInspector}.
 */
export class BrowserViewInspector extends Disposable {

	private readonly _onDidSelectElement = this._register(new Emitter<IElementData>());
	readonly onDidSelectElement: Event<IElementData> = this._onDidSelectElement.event;

	private readonly _onDidChangeElementSelectionActive = this._register(new Emitter<boolean>());
	readonly onDidChangeElementSelectionActive: Event<boolean> = this._onDidChangeElementSelectionActive.event;

	private _elementSelectionActive = false;
	get isElementSelectionActive(): boolean { return this._elementSelectionActive; }

	private readonly _activeSelection = this._register(new MutableDisposable<IActiveSelection>());
	private _theme: IBrowserViewTheme = {};

	// Area selection — drag-to-select a rectangle on the top frame.
	// `onDidPickArea` fires exactly once per session, terminating it.
	// The rectangle is undefined when the picker is cancelled (ESC, zero-area drag,
	// external toggle off, navigation, or supersession by element selection).
	// Consumers should listen to this single event instead of trying to reconcile
	// rect vs. activation events across the IPC boundary — those two events travel
	// through separate channels and can be delivered out of order.
	private readonly _onDidPickArea = this._register(new Emitter<IBrowserViewRect | undefined>());
	readonly onDidPickArea: Event<IBrowserViewRect | undefined> = this._onDidPickArea.event;

	private readonly _onDidChangeAreaSelectionActive = this._register(new Emitter<boolean>());
	readonly onDidChangeAreaSelectionActive: Event<boolean> = this._onDidChangeAreaSelectionActive.event;

	private _areaSelectionActive = false;
	get isAreaSelectionActive(): boolean { return this._areaSelectionActive; }

	private readonly _activeAreaSelection = this._register(new MutableDisposable<IActiveSelection>());

	private readonly _registry = this._register(new FrameInspectorRegistry());

	constructor(private readonly browser: BrowserView) {
		super();

		const webContents = this.browser.webContents;

		// Wire up inspector adoption from the registry
		this._register(this._registry.onDidAdopt(inspector => this._onInspectorAdopted(inspector)));

		// Navigation destroys preload overlays and CDP state
		const onNavigated = () => {
			this._activeSelection.clear();
			this._activeAreaSelection.clear();
		};
		webContents.on('did-navigate', onNavigated);
		this._register({ dispose: () => webContents.removeListener('did-navigate', onNavigated) });

		// Preload ready — the key correlation point between WebFrameMain and CDP target
		const onIpcMessage = (_event: Electron.Event, channel: string, ...args: unknown[]) => {
			const senderFrame = (_event as { senderFrame?: Electron.WebFrameMain }).senderFrame;
			if (channel === 'vscode:browserView:preloadReady') {
				if (!senderFrame) {
					return;
				}
				const frameToken = args[0] as string;
				if (!frameToken) {
					return;
				}

				// Apply theme immediately regardless of inspector state
				senderFrame.postMessage('vscode:browserView:setTheme', this._theme);

				this._registry.notifyFrameReady(senderFrame, frameToken);

				// If area selection was activated before the main-frame preload
				// finished wiring up its IPC listeners (e.g. right after a
				// navigation), the original `startAreaPicker` postMessage was
				// dropped. Replay it now so the picker actually appears instead
				// of leaving the model reporting active with no visible overlay.
				if (senderFrame === webContents.mainFrame && this._activeAreaSelection.value) {
					try {
						senderFrame.postMessage('vscode:browserView:startAreaPicker', undefined);
					} catch {
						// Frame may be gone — ignore.
					}
				}
			} else if (channel === 'vscode:browserView:areaPicked') {
				// Area selection is scoped to the top frame — the user-drawn
				// rectangle is in main-frame viewport coordinates.
				if (senderFrame !== webContents.mainFrame) {
					return;
				}
				const rect = args[0] as IBrowserViewRect | undefined;
				const validRect = rect && rect.width > 0 && rect.height > 0 ? rect : undefined;
				this._finishAreaPick(validRect);
			} else if (channel === 'vscode:browserView:areaPickStopped') {
				if (senderFrame !== webContents.mainFrame) {
					return;
				}
				this._finishAreaPick(undefined);
			}
		};
		webContents.on('ipc-message', onIpcMessage);
		this._register({ dispose: () => webContents.removeListener('ipc-message', onIpcMessage) });

		// Cross-origin (OOPIF) targets get their own session — watch it for contexts
		this._register(this.browser.debugger.onTargetDiscovered(async ({ targetId, type }) => {
			if (type === 'iframe') {
				try {
					const session = await this.browser.debugger.attachToTarget(targetId);
					this._watchSession(session);
				} catch {
					return;
				}
			}
		}));

		// Attach the main debugger session and watch it for contexts
		this.browser.debugger.attach().then(conn => this._watchSession(conn)).catch(() => { });
	}

	/**
	 * Watch a CDP session for execution contexts. When a default context appears,
	 * probes for the preload token and correlates with the pending WebFrameMain.
	 *
	 * Called for every session: the main page session (sees same-origin frames)
	 * and each cross-origin target session (sees only its own frame).
	 */
	private _watchSession(session: ICDPConnection): void {
		this._register(session.onEvent(async event => {
			if (event.method === 'Runtime.executionContextCreated') {
				const context = (event.params as {
					context: {
						uniqueId: string;
						auxData?: { isDefault?: boolean; frameId?: string };
					};
				}).context;

				if (!context?.auxData?.isDefault || !context.auxData.frameId) {
					return;
				}

				const frameId = context.auxData.frameId;
				const uniqueContextId = context.uniqueId;

				// Probe for the preload token in this context
				try {
					const { result } = await session.sendCommand('Runtime.evaluate', {
						expression: 'window.__vscode_helpers?.getFrameToken?.()',
						returnByValue: true,
						uniqueContextId,
					}) as { result: { value?: string } };

					const token = result.value;
					if (!token) {
						return;
					}

					this._registry.notifyContextDiscovered(session, uniqueContextId, frameId, token);
				} catch {
					// Context may have been destroyed by now — ignore.
				}
			} else if (event.method === 'Page.frameDetached') {
				const frameId = (event.params as { frameId?: string })?.frameId;
				if (frameId) {
					this._registry.disposeByFrameId(frameId);
				}
			} else if (event.method === 'Runtime.executionContextsCleared') {
				// Navigation cleared all contexts — dispose inspectors owned by this session
				this._registry.disposeBySession(session);
			}
		}));

		Event.once(session.onClose)(() => {
			this._registry.disposeBySession(session);
		});

		// Enable Runtime + Page to start receiving context and frame events
		session.sendCommand('Runtime.enable').catch(() => { });
		session.sendCommand('Page.enable').catch(() => { });
	}

	/**
	 * Called by the registry when a frame inspector is fully adopted.
	 * Wires its events to this orchestrator.
	 */
	private _onInspectorAdopted(inspector: BrowserViewFrameInspector): void {
		inspector.onDidInspectElement(async nodeData => {
			this._activeSelection.clear();
			try {
				const offset = await this._getFrameOffsetInPage(inspector.frame);
				nodeData = this._offsetElementData(nodeData, offset);
			} catch {
				// Best effort.
			}
			this._onDidSelectElement.fire(nodeData);
		});

		// When a frame's preload stops picking, stop all other frames too
		inspector.onDidStopPicking(() => {
			this._activeSelection.clear();
		});

		// If element selection is currently active, start it on the new frame
		if (this._activeSelection.value) {
			inspector.startInspection().catch(() => { });
		}

		inspector.setTheme(this._theme);
	}

	setTheme(theme: IBrowserViewTheme): void {
		this._theme = theme;
		// Broadcast to all known inspectors
		for (const inspector of this._registry.inspectors) {
			inspector.setTheme(theme);
		}
	}

	/**
	 * Toggle element selection mode across all frames.
	 */
	async toggleElementSelection(enabled?: boolean): Promise<void> {
		const newEnabled = enabled ?? !this._elementSelectionActive;
		if (newEnabled === this._elementSelectionActive) {
			return;
		}

		if (!newEnabled) {
			this._activeSelection.clear();
			return;
		}

		// Element and area selection are mutually exclusive — enabling one
		// cancels the other so both pickers never overlay the page at once.
		this._activeAreaSelection.clear();

		const start = () => Promise.all([...this._registry.inspectors].map(i => i.startInspection()));
		const stop = () => Promise.all([...this._registry.inspectors].map(i => i.stopInspection()));

		const selection: IActiveSelection = {
			dispose: () => {
				if (this._activeSelection.value === selection) {
					this._elementSelectionActive = false;
					this._onDidChangeElementSelectionActive.fire(false);
					this._activeSelection.clearAndLeak();
					void stop().catch(() => { });
				}
			}
		};
		this._activeSelection.value = selection;

		try {
			await start();
			if (this._activeSelection.value === selection) {
				this._elementSelectionActive = true;
				this._onDidChangeElementSelectionActive.fire(true);
			}
		} catch {
			this._activeSelection.clear();
		}
	}

	/**
	 * Toggle drag-to-select area picking on the top frame only.
	 * The picker reports the literal user-drawn rectangle (or `undefined` on cancellation)
	 * via {@link onDidPickArea}; no DOM elements are inspected.
	 */
	async toggleAreaSelection(enabled?: boolean): Promise<void> {
		const newEnabled = enabled ?? !this._areaSelectionActive;
		if (newEnabled === this._areaSelectionActive) {
			return;
		}

		if (!newEnabled) {
			this._activeAreaSelection.clear();
			return;
		}

		// Element and area selection are mutually exclusive — enabling one
		// cancels the other so both pickers never overlay the page at once.
		this._activeSelection.clear();

		const mainFrame = this.browser.webContents.mainFrame;
		const start = () => { mainFrame.postMessage('vscode:browserView:startAreaPicker', undefined); };
		const stop = () => { try { mainFrame.postMessage('vscode:browserView:stopAreaPicker', undefined); } catch { /* frame may be gone */ } };

		const selection: IActiveSelection = {
			dispose: () => {
				// External cancellation (toggleAreaSelection(false), navigation, element
				// selection takeover). The IPC-driven termination paths use clearAndLeak
				// inside `_finishAreaPick`, so reaching here means the picker is still
				// running in the page and we need to tell it to stop.
				stop();
				this._finishAreaPick(undefined);
			}
		};
		this._activeAreaSelection.value = selection;

		try {
			start();
			if (this._activeAreaSelection.value === selection) {
				this._areaSelectionActive = true;
				this._onDidChangeAreaSelectionActive.fire(true);
			}
		} catch {
			this._activeAreaSelection.clear();
		}
	}

	/**
	 * Terminate the current area-pick session, firing `onDidPickArea` exactly once.
	 * No-op if no session is active. Uses `clearAndLeak` to avoid recursing into
	 * the IActiveSelection.dispose path.
	 */
	private _finishAreaPick(rect: IBrowserViewRect | undefined): void {
		if (!this._areaSelectionActive && !this._activeAreaSelection.value) {
			return;
		}
		const wasActive = this._areaSelectionActive;
		this._areaSelectionActive = false;
		this._activeAreaSelection.clearAndLeak();
		this._onDidPickArea.fire(rect);
		if (wasActive) {
			this._onDidChangeAreaSelectionActive.fire(false);
		}
	}

	/**
	 * Resolve a handle to an element. Routes to the correct frame inspector.
	 */
	getElementHandle(id: string, frame: Electron.WebFrameMain): IElementHandle | undefined {
		return this._registry.getByFrame(frame)?.getElementHandle(id);
	}

	async getVisualViewportScale(frame: Electron.WebFrameMain = this.browser.webContents.mainFrame): Promise<number> {
		return this._registry.getByFrame(frame)?.getVisualViewportScale() ?? 1;
	}

	/**
	 * Compute the cumulative offset of a frame relative to the top-level page.
	 * Walks up the frame hierarchy using the parent's CDP session to query the
	 * iframe element's box model via `DOM.getFrameOwner` + `DOM.getBoxModel`.
	 * Works for both same-origin and cross-origin frames.
	 */
	private async _getFrameOffsetInPage(frame: Electron.WebFrameMain): Promise<{ x: number; y: number }> {
		const mainFrame = this.browser.webContents.mainFrame;
		let x = 0;
		let y = 0;
		let current = frame;

		while (current !== mainFrame) {
			const parent = current.parent;
			if (!parent) {
				break;
			}

			const childInspector = this._registry.getByFrame(current);
			const parentInspector = this._registry.getByFrame(parent);
			if (!childInspector || !parentInspector) {
				break;
			}

			try {
				const childFrameId = childInspector.frameId;

				// Ask the parent session for the iframe element that owns this frame
				const frameOwner = await parentInspector.connection.sendCommand('DOM.getFrameOwner', {
					frameId: childFrameId,
				}) as { backendNodeId: number };

				// Get the iframe element's box model in the parent's coordinate space
				const boxModel = await parentInspector.connection.sendCommand('DOM.getBoxModel', {
					backendNodeId: frameOwner.backendNodeId,
				}) as { model: { content: number[] } };

				// content quad: [x1,y1, x2,y2, x3,y3, x4,y4] — top-left is first pair
				const content = boxModel.model.content;
				x += content[0];
				y += content[1];
			} catch {
				break;
			}

			current = parent;
		}

		return { x, y };
	}

	/**
	 * Offset element data bounds by a frame offset.
	 */
	private _offsetElementData(data: IElementData, offset: { x: number; y: number }): IElementData {
		if (offset.x === 0 && offset.y === 0) {
			return data;
		}
		return {
			...data,
			bounds: {
				x: data.bounds.x + offset.x,
				y: data.bounds.y + offset.y,
				width: data.bounds.width,
				height: data.bounds.height,
			}
		};
	}
}


interface IPendingContext {
	readonly session: ICDPConnection;
	readonly uniqueContextId: string;
	readonly frameId: string;
}

/**
 * Tracks the two-sided correlation between preload tokens (from WebFrameMain IPC)
 * and CDP execution contexts, and indexes adopted inspectors for O(1) lookup by
 * frame, frameId, or owning session.
 */
class FrameInspectorRegistry extends Disposable {

	private readonly _onDidAdopt = this._register(new Emitter<BrowserViewFrameInspector>());
	readonly onDidAdopt: Event<BrowserViewFrameInspector> = this._onDidAdopt.event;

	/** Pending halves waiting for their counterpart. */
	private readonly _pendingFrames = new Map<string, Electron.WebFrameMain>();
	private readonly _pendingSessions = new Map<string, IPendingContext>();

	/** Adopted inspectors indexed multiple ways. */
	private readonly _all = new Set<BrowserViewFrameInspector>();
	private readonly _byFrame = new WeakMap<Electron.WebFrameMain, BrowserViewFrameInspector>();
	private readonly _byFrameId = new Map<string, BrowserViewFrameInspector>();
	private readonly _bySession = new Map<ICDPConnection, Set<BrowserViewFrameInspector>>();

	get inspectors(): Iterable<BrowserViewFrameInspector> { return this._all; }

	getByFrame(frame: Electron.WebFrameMain): BrowserViewFrameInspector | undefined {
		return this._byFrame.get(frame);
	}

	/**
	 * Called when a preload script signals readiness with a token.
	 * If a matching CDP context was already discovered, adopts immediately.
	 */
	notifyFrameReady(frame: Electron.WebFrameMain, token: string): void {
		const pending = this._pendingSessions.get(token);
		if (pending) {
			this._pendingSessions.delete(token);
			this._adopt(pending.session, pending.uniqueContextId, pending.frameId, frame);
		} else {
			this._pendingFrames.set(token, frame);
		}
	}

	/**
	 * Called when a CDP execution context is discovered and its preload token probed.
	 * If a matching WebFrameMain was already registered, adopts immediately.
	 */
	notifyContextDiscovered(session: ICDPConnection, uniqueContextId: string, frameId: string, token: string): void {
		const frame = this._pendingFrames.get(token);
		if (frame) {
			this._pendingFrames.delete(token);
			this._adopt(session, uniqueContextId, frameId, frame);
		} else {
			this._pendingSessions.set(token, { session, uniqueContextId, frameId });
		}
	}

	/** Dispose the inspector owning the given CDP frameId, if any. Also cleans pending entries. */
	disposeByFrameId(frameId: string): void {
		this._byFrameId.get(frameId)?.dispose();
		// Remove pending session entries whose frameId matches the detached frame
		for (const [token, pending] of this._pendingSessions) {
			if (pending.frameId === frameId) {
				this._pendingSessions.delete(token);
			}
		}
		// Remove any pending frame entries whose frame is now detached/destroyed
		for (const [token, frame] of this._pendingFrames) {
			if (frame.detached || frame.isDestroyed()) {
				this._pendingFrames.delete(token);
			}
		}
	}

	/** Dispose all inspectors whose connection is the given session and clear related pending state. */
	disposeBySession(session: ICDPConnection): void {
		const set = this._bySession.get(session);
		if (set) {
			for (const inspector of [...set]) {
				inspector.dispose();
			}
		}
		for (const [token, pending] of this._pendingSessions) {
			if (pending.session === session) {
				this._pendingSessions.delete(token);
			}
		}
	}

	private _adopt(
		session: ICDPConnection,
		uniqueContextId: string,
		frameId: string,
		frame: Electron.WebFrameMain,
	): void {
		// Guard: frame may have been destroyed between IPC and context match
		if (frame.detached || frame.isDestroyed()) {
			return;
		}

		const inspector = new BrowserViewFrameInspector(session, frame, uniqueContextId, frameId);

		this._all.add(inspector);
		this._byFrame.set(frame, inspector);
		this._byFrameId.set(frameId, inspector);

		let sessionSet = this._bySession.get(session);
		if (!sessionSet) {
			sessionSet = new Set();
			this._bySession.set(session, sessionSet);
		}
		sessionSet.add(inspector);

		inspector.onWillDispose(() => {
			this._all.delete(inspector);
			this._byFrame.delete(frame);
			this._byFrameId.delete(frameId);
			const s = this._bySession.get(session);
			if (s) {
				s.delete(inspector);
				if (s.size === 0) {
					this._bySession.delete(session);
				}
			}
		});

		this._onDidAdopt.fire(inspector);
	}

	override dispose(): void {
		for (const inspector of [...this._all]) {
			inspector.dispose();
		}
		this._pendingFrames.clear();
		this._pendingSessions.clear();
		super.dispose();
	}
}
